import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MessageSquare,
  MessageCircle,
  Loader2,
  Search,
  Send,
  RefreshCw,
  Facebook,
  Instagram,
  User,
  Link2,
  X,
  Inbox,
  ChevronLeft,
  Settings,
  CornerDownRight,
  UserPlus,
  Archive,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Conversation List Item ───
function ConversationItem({ conv, isActive, onClick }) {
  const platformIcon = conv.platform === 'instagram'
    ? <Instagram className="w-4 h-4 text-pink-500" />
    : <Facebook className="w-4 h-4 text-blue-600" />;

  return (
    <button
      data-testid={`conversation-item-${conv.id}`}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-border/50 transition-colors hover:bg-muted/50 ${
        isActive ? 'bg-muted/80 border-l-2 border-l-primary' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
            {platformIcon}
          </div>
          {conv.unread_count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-background" data-testid={`unread-dot-${conv.id}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>
              {conv.participant_name || conv.participant_id}
            </span>
            {conv.unread_count > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] text-xs flex-shrink-0">
                {conv.unread_count}
              </Badge>
            )}
          </div>
          <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
            {conv.last_message || 'No messages yet'}
          </p>
          {conv.assigned_user_name && (
            <p className="text-[10px] text-blue-500 mt-0.5 truncate">{conv.assigned_user_name}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground">
              {conv.last_message_time ? formatDateTime(conv.last_message_time) : ''}
            </span>
            {conv.customer_name && (
              <Badge variant="outline" className="text-[10px] h-4 px-1">
                <Link2 className="w-2.5 h-2.5 mr-0.5" />
                {conv.customer_name}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Message Bubble ───
function MessageBubble({ msg, pageId }) {
  const isOutbound = msg.direction === 'outbound';
  return (
    <div
      data-testid={`message-${msg.id}`}
      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}
    >
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isOutbound
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted rounded-bl-md'
        }`}
      >
        {msg.text && <p className="text-sm whitespace-pre-wrap">{msg.text}</p>}
        {msg.attachments?.map((att, i) => (
          <div key={i} className="mt-1">
            {att.type === 'image' ? (
              <img src={att.url} alt="attachment" className="max-w-full rounded-lg max-h-48" />
            ) : att.url ? (
              <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                View Attachment ({att.type})
              </a>
            ) : null}
          </div>
        ))}
        <p className={`text-[10px] mt-1 ${isOutbound ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
          {msg.timestamp ? formatDateTime(msg.timestamp) : ''}
          {msg.sent_by && <span className="ml-1">by {msg.sent_by}</span>}
        </p>
      </div>
    </div>
  );
}


// ─── Comment Card ───
function CommentCard({ c, replyingId, replyText, setReplyText, onOpenReply, onSubmitReply, replyLoading, onCreateLead, leadLoadingId }) {
  const isIG = c.platform === 'instagram';
  return (
    <div data-testid={`comment-${c.id}`} className="border-b border-border/50 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
          {isIG ? <Instagram className="w-4 h-4 text-pink-500" /> : <Facebook className="w-4 h-4 text-blue-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{c.from_name}</span>
            {!c.read && c.direction === 'inbound' && (
              <span className="w-2 h-2 bg-blue-500 rounded-full" data-testid={`comment-unread-${c.id}`} />
            )}
            <span className="text-[10px] text-muted-foreground">{c.created_time ? formatDateTime(c.created_time) : ''}</span>
          </div>
          {c.post_message && (
            <p className="text-[11px] text-muted-foreground italic mt-0.5 truncate">on: {c.post_message}</p>
          )}
          <p className="text-sm mt-1 whitespace-pre-wrap break-words">{c.text}</p>
          <div className="flex items-center gap-3 mt-1.5">
            {c.permalink && (
              <a href={c.permalink} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 underline">
                View on {isIG ? 'Instagram' : 'Facebook'}
              </a>
            )}
            {c.direction === 'inbound' && !c.replied && (
              <button
                data-testid={`comment-reply-btn-${c.id}`}
                onClick={() => onOpenReply(c)}
                className="text-[11px] text-primary font-medium hover:underline"
              >
                Reply
              </button>
            )}
            {c.replied && (
              <span className="text-[11px] text-green-600">✓ Replied{c.reply_by ? ` by ${c.reply_by}` : ''}</span>
            )}
            {c.lead_customer_id ? (
              <span className="text-[11px] text-blue-600 flex items-center gap-0.5" data-testid={`comment-lead-done-${c.id}`}>
                <UserPlus className="w-3 h-3" /> Lead created
              </span>
            ) : (
              <button
                data-testid={`comment-create-lead-${c.id}`}
                onClick={() => onCreateLead(c)}
                disabled={leadLoadingId === c.id}
                className="text-[11px] text-primary font-medium hover:underline flex items-center gap-0.5 disabled:opacity-50"
              >
                {leadLoadingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />} Create lead
              </button>
            )}
          </div>
          {c.replied && c.reply_text && (
            <div className="mt-2 ml-1 pl-3 border-l-2 border-green-200 text-xs text-muted-foreground">
              <CornerDownRight className="w-3 h-3 inline mr-1" />{c.reply_text}
            </div>
          )}
          {replyingId === c.id && (
            <div className="flex items-end gap-2 mt-2">
              <Input
                data-testid={`comment-reply-input-${c.id}`}
                autoFocus
                placeholder="Write a public reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmitReply(c.id); } }}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                className="h-8"
                data-testid={`comment-reply-send-${c.id}`}
                disabled={!replyText.trim() || replyLoading}
                onClick={() => onSubmitReply(c.id)}
              >
                {replyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Main Page ───
export function SocialInbox() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [stats, setStats] = useState({});
  const [linkDialog, setLinkDialog] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const [users, setUsers] = useState([]);
  // Comments
  const [view, setView] = useState('messages'); // 'messages' | 'comments'
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentUnread, setCommentUnread] = useState(0);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [leadLoadingId, setLeadLoadingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedTotal, setArchivedTotal] = useState(0);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch users for assignment
  useEffect(() => {
    api.get('/admin/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (platform !== 'all') params.platform = platform;
      if (search) params.search = search;
      if (showArchived) params.archived = true;
      const { data } = await api.get('/messaging/conversations', { params });
      setConversations(data.conversations || []);
      setArchivedTotal(data.archived_total || 0);
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
  }, [platform, search, showArchived]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/messaging/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load messaging stats', err);
    }
  }, []);

  // Fetch comments (FB + IG)
  const fetchComments = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (platform !== 'all') params.platform = platform === 'messenger' ? 'facebook' : platform;
      if (search) params.search = search;
      const { data } = await api.get('/messaging/comments', { params });
      setComments(data.comments || []);
      setCommentUnread(data.unread || 0);
    } catch (err) {
      console.error('Failed to load comments', err);
    }
  }, [platform, search]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchConversations(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchConversations, fetchStats]);

  // Poll conversations every 10s (backend auto-syncs Meta separately)
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchConversations();
      fetchStats();
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchStats]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (!activeConv) return;
    setMessagesLoading(true);
    api.get(`/messaging/conversations/${activeConv.id}/messages`, { params: { limit: 100 } })
      .then(({ data }) => {
        setMessages(data.messages || []);
        // Mark as read
        if (activeConv.unread_count > 0) {
          api.post(`/messaging/conversations/${activeConv.id}/read`).then(() => {
            fetchConversations();
            fetchStats();
          });
        }
      })
      .catch(() => toast.error('Failed to load messages'))
      .finally(() => setMessagesLoading(false));
  }, [activeConv?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!messageText.trim() || !activeConv) return;
    setSendLoading(true);
    try {
      await api.post('/messaging/send', {
        conversation_id: activeConv.id,
        text: messageText.trim(),
      });
      setMessageText('');
      // Re-fetch messages
      const { data } = await api.get(`/messaging/conversations/${activeConv.id}/messages`, { params: { limit: 100 } });
      setMessages(data.messages || []);
      fetchConversations();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send message';
      toast.error(detail);
    } finally {
      setSendLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const { data } = await api.post('/messaging/sync');
      toast.success(`Synced ${data.synced_conversations || 0} conversation(s), ${data.synced_comments || 0} comment(s) from Meta`);
      fetchConversations();
      fetchStats();
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  const openReply = (c) => {
    setReplyingId(c.id);
    setReplyText('');
    if (!c.read) api.post(`/messaging/comments/${c.id}/read`).then(() => fetchComments()).catch(() => {});
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      await api.post(`/messaging/comments/${commentId}/reply`, { text: replyText.trim() });
      toast.success('Reply posted');
      setReplyingId(null);
      setReplyText('');
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCreateLead = async (c) => {
    setLeadLoadingId(c.id);
    try {
      const { data } = await api.post(`/messaging/comments/${c.id}/create-lead`);
      toast.success(data.message || 'Lead created', {
        action: { label: 'View', onClick: () => navigate(`/customers/${data.customer_id}`) },
        duration: 10000,
      });
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setLeadLoadingId(null);
    }
  };

  const handleLinkCustomer = async (customerId) => {
    try {
      await api.post(`/messaging/conversations/${activeConv.id}/link-customer`, { customer_id: customerId });
      toast.success('Customer linked');
      setLinkDialog(false);
      fetchConversations();
      // Refresh active conv
      const { data } = await api.get(`/messaging/conversations/${activeConv.id}`);
      setActiveConv(data);
    } catch (err) {
      toast.error('Failed to link customer');
    }
  };

  const handleAssignUser = async (userId) => {
    try {
      await api.post(`/messaging/conversations/${activeConv.id}/assign`, { user_id: userId });
      const user = users.find(u => u.id === userId);
      toast.success(userId ? `Assigned to ${user?.name || 'user'}` : 'Unassigned');
      fetchConversations();
      setActiveConv(prev => ({ ...prev, assigned_user_id: userId, assigned_user_name: user?.name || '' }));
    } catch {
      toast.error('Failed to assign');
    }
  };


  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    try {
      const { data } = await api.get('/customers', { params: { search: q, limit: 10 } });
      setCustomerResults(data.customers || []);
    } catch {
      setCustomerResults([]);
    }
  };

  const selectConversation = (conv) => {
    setActiveConv(conv);
    setMobileShowThread(true);
  };

  return (
    <Layout>
      <div data-testid="social-inbox-page" className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold">Social Inbox</h1>
            {stats.unread_conversations > 0 && (
              <Badge variant="destructive" data-testid="unread-badge">{stats.unread_conversations} unread</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncLoading}
              data-testid="sync-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncLoading ? 'animate-spin' : ''}`} />
              Sync
            </Button>
          </div>
        </div>

        {/* Main content: sidebar + thread */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar / conversation list */}
          <div className={`w-full sm:w-80 lg:w-96 border-r border-border flex flex-col bg-background ${mobileShowThread ? 'hidden sm:flex' : 'flex'}`}>
            {/* Filters */}
            <div className="p-3 space-y-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="conversation-search"
                  placeholder="Search conversations..."
                  className="pl-9 h-9 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Tabs value={platform} onValueChange={setPlatform}>
                <TabsList className="w-full grid grid-cols-3 h-8">
                  <TabsTrigger value="all" className="text-xs" data-testid="tab-all">
                    All ({stats.total_conversations || 0})
                  </TabsTrigger>
                  <TabsTrigger value="messenger" className="text-xs" data-testid="tab-messenger">
                    <Facebook className="w-3 h-3 mr-1" /> Messenger
                  </TabsTrigger>
                  <TabsTrigger value="instagram" className="text-xs" data-testid="tab-instagram">
                    <Instagram className="w-3 h-3 mr-1" /> Instagram
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {(archivedTotal > 0 || showArchived) && (
                <button
                  data-testid="toggle-archived-btn"
                  onClick={() => setShowArchived((s) => !s)}
                  className={`w-full flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-md border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-border text-muted-foreground hover:bg-muted'}`}
                >
                  <Archive className="w-3.5 h-3.5" />
                  {showArchived ? 'Viewing archived — back to inbox' : `Archived (${archivedTotal})`}
                </button>
              )}
            </div>

            {/* Conversation list */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Inbox className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click "Sync" to pull conversations from your Facebook Page, or messages will appear here as they arrive via webhook.
                  </p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conv={conv}
                    isActive={activeConv?.id === conv.id}
                    onClick={() => selectConversation(conv)}
                  />
                ))
              )}
            </ScrollArea>
          </div>

          {/* Message Thread */}
          <div className={`flex-1 flex flex-col bg-muted/30 ${!mobileShowThread ? 'hidden sm:flex' : 'flex'}`}>
            {activeConv ? (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-background">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="sm:hidden h-8 w-8"
                    onClick={() => setMobileShowThread(false)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                    {activeConv.platform === 'instagram'
                      ? <Instagram className="w-4 h-4 text-pink-500" />
                      : <Facebook className="w-4 h-4 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {activeConv.participant_name || activeConv.participant_id}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">{activeConv.platform}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Assign user */}
                    <Select
                      value={activeConv.assigned_user_id || 'unassigned'}
                      onValueChange={(v) => handleAssignUser(v === 'unassigned' ? '' : v)}
                    >
                      <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="assign-user-select">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {activeConv.customer_id ? (
                      <Badge variant="secondary" className="text-xs">
                        <Link2 className="w-3 h-3 mr-1" />
                        {activeConv.customer_name || 'Linked'}
                      </Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setLinkDialog(true)}
                        data-testid="link-customer-btn"
                      >
                        <Link2 className="w-3 h-3 mr-1" /> Link Customer
                      </Button>
                    )}
                  </div>
                </div>

                {/* Messages area */}
                <ScrollArea className="flex-1 p-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No messages in this conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <MessageBubble key={msg.id} msg={msg} pageId={activeConv.page_id} />
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </ScrollArea>

                {/* Compose */}
                <div className="p-3 border-t border-border bg-background">
                  <div className="flex items-end gap-2">
                    <Input
                      data-testid="message-input"
                      placeholder="Type a message..."
                      className="flex-1"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      onClick={handleSend}
                      disabled={!messageText.trim() || sendLoading}
                      data-testid="send-btn"
                    >
                      {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Empty state */
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium text-base mb-1">Select a conversation</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Choose a conversation from the left to view messages and reply.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link Customer Dialog */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to CRM Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="link-customer-search"
              placeholder="Search customers by name, email..."
              value={customerSearch}
              onChange={(e) => searchCustomers(e.target.value)}
            />
            <ScrollArea className="max-h-60">
              {customerResults.length === 0 && customerSearch.length >= 2 && (
                <p className="text-sm text-muted-foreground text-center py-4">No customers found</p>
              )}
              {customerResults.map((c) => (
                <button
                  key={c.id}
                  data-testid={`link-customer-option-${c.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-muted rounded-md flex items-center gap-2"
                  onClick={() => handleLinkCustomer(c.id)}
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </div>
                </button>
              ))}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
